import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHomeData } from '@/hooks/useTranslatedData';

export default function HeroSection() {
  const { t } = useTranslation('common');
  const heroData = useHomeData().hero;
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 md:pt-0"
    >
      {/* KV Background */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=Abstract%20minimalist%20wellness%20brand%20hero%20background%20soft%20sage%20green%20and%20warm%20cream%20palette%20delicate%20floating%20botanical%20silhouettes%20and%20translucent%20molecular%20structures%20subtle%20organic%20geometric%20patterns%20modern%20laboratory%20aesthetic%20gentle%20light%20diffusion%20no%20text%20clean%20composition%20premium%20health%20food%20brand%20key%20visual%20high%20resolution%20digital%20art&width=1920&height=1080&seq=hero-kv-bg-001&orientation=landscape')`
        }}
      />
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-brand-cream/90 via-brand-cream/80 to-brand-cream/95" />

      <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
        <div className="text-center">
          {/* Main Title */}
          <h1 className="mb-6">
            <span
              className={`block text-4xl md:text-6xl lg:text-7xl font-bold text-brand-charcoal tracking-tight transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              {heroData.titleLine1}
            </span>
            <span
              className={`block text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight transition-all duration-700 delay-150 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ color: '#2D5A3D' }}
            >
              {heroData.titleLine2}
            </span>
          {heroData.titleLine3 && (
            <span
              className={`block text-4xl md:text-6xl lg:text-7xl font-bold text-brand-charcoal tracking-tight transition-all duration-700 delay-300 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              {heroData.titleLine3}
            </span>
          )}
          </h1>

          {/* Subtitle */}
          <p
            className={`text-base md:text-lg text-brand-gray font-medium mb-8 transition-all duration-700 delay-500 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            {heroData.subtitle}
          </p>


          {/* CTA Buttons */}
          <div
            className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 delay-600 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <Link
              to="/products"
              className="flex items-center gap-2 px-8 py-3.5 bg-brand-charcoal text-white text-sm font-semibold rounded-full hover:bg-brand-stone transition-colors whitespace-nowrap"
            >
              {heroData.ctaPrimary}
              <i className="ri-arrow-right-line text-sm"></i>
            </Link>
            <Link
              to="/about"
              className="text-sm font-medium text-brand-gray hover:text-brand-charcoal transition-colors whitespace-nowrap flex items-center gap-1.5"
            >
              {heroData.ctaSecondary}
              <i className="ri-arrow-right-line text-xs"></i>
            </Link>
          </div>

          {/* Product Cards Visual */}
          <div
            className={`mt-10 md:mt-14 flex items-center justify-center gap-3 md:gap-4 perspective-1000 transition-all duration-1000 delay-800 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
          >
            {/* Card 1 */}
            <div className="w-28 md:w-40 h-36 md:h-48 bg-brand-mint-light rounded-2xl shadow-lg flex flex-col items-center justify-center transform -rotate-6 hover:rotate-0 hover:-translate-y-2 transition-all duration-500 cursor-pointer">
              <div className="w-10 h-10 flex items-center justify-center mb-2">
                <i className="ri-capsule-line text-xl text-brand-mint-dark"></i>
              </div>
              <span className="text-xs font-semibold text-brand-green">{heroData.card1}</span>
              <span className="text-[10px] text-brand-gray mt-1">{heroData.card1Sub}</span>
            </div>
            {/* Card 2 - center, larger */}
            <div className="w-32 md:w-48 h-44 md:h-56 bg-white rounded-2xl shadow-xl flex flex-col items-center justify-center transform z-10 hover:-translate-y-3 transition-all duration-500 cursor-pointer border border-brand-gray-pale/30">
              <div className="w-12 h-12 flex items-center justify-center mb-2">
                <i className="ri-flask-line text-2xl text-brand-coral"></i>
              </div>
              <span className="text-sm font-semibold text-brand-charcoal">{heroData.card2}</span>
              <span className="text-xs text-brand-gray mt-1">{heroData.card2Sub}</span>
            </div>
            {/* Card 3 */}
            <div className="w-28 md:w-40 h-36 md:h-48 bg-brand-green-light rounded-2xl shadow-lg flex flex-col items-center justify-center transform rotate-6 hover:rotate-0 hover:-translate-y-2 transition-all duration-500 cursor-pointer">
              <div className="w-10 h-10 flex items-center justify-center mb-2">
                <i className="ri-seedling-line text-xl text-brand-green"></i>
              </div>
              <span className="text-xs font-semibold text-brand-green">{heroData.card3}</span>
              <span className="text-[10px] text-brand-gray mt-1">{heroData.card3Sub}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-brand-gray-light">
        <span className="text-xs tracking-widest">{t('misc.scroll')}</span>
        <div className="w-5 h-8 rounded-full border border-brand-gray-light/40 flex items-start justify-center pt-2">
          <div className="w-1 h-1.5 rounded-full bg-brand-gray-light animate-bounce" />
        </div>
      </div>
    </section>
  );
}